import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  FileText, ArrowUp, ArrowDown, Minus, Calendar
} from "lucide-react";

export default function DailyReportTab({ selectedProject }) {
  const { api } = useAuth();
  const [report, setReport] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchReport(); }, [date]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get("/checklist/report", { params: { date } });
      setReport(res.data);
    } catch {} finally { setLoading(false); }
  };

  const StatCard = ({ label, value, prev, color }) => {
    const diff = value - (prev || 0);
    return (
      <div className="report-stat-card">
        <div className="report-stat-label">{label}</div>
        <div className="report-stat-value" style={{ color: color || '#e5e5e5' }}>{value}</div>
        {prev !== undefined && (
          <div className={`report-stat-diff ${diff > 0 ? 'up' : diff < 0 ? 'down' : ''}`}>
            {diff > 0 ? <ArrowUp size={11} /> : diff < 0 ? <ArrowDown size={11} /> : <Minus size={11} />}
            {Math.abs(diff)} vs yesterday
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="daily-report-section" data-testid="daily-report-section">
      <div className="section-header">
        <h2 className="section-title"><FileText size={20} color="#ff4500" /> Daily Report</h2>
        <div className="date-picker">
          <Calendar size={14} color="#6b7280" />
          <input type="date" className="filter-input" value={date} onChange={e => setDate(e.target.value)} data-testid="report-date-picker" style={{ width: 160 }} />
        </div>
      </div>

      {report && (
        <>
          <div className="report-section">
            <h3 className="subsection-title">Today ({report.date})</h3>
            <div className="report-stats-grid">
              <StatCard label="Total" value={report.today.total} prev={report.yesterday.total} />
              <StatCard label="Posts" value={report.today.posts} prev={report.yesterday.posts} />
              <StatCard label="Comments" value={report.today.comments} prev={report.yesterday.comments} />
              <StatCard label="Live" value={report.today.live} prev={report.yesterday.live} color="var(--live-green)" />
              <StatCard label="Deleted" value={report.today.deleted} prev={report.yesterday.deleted} color="var(--deleted-red)" />
              <StatCard label="To Be Assigned" value={report.today.imported || 0} prev={report.yesterday.imported || 0} color="#f59e0b" />
              <StatCard label="Assigned" value={report.today.assigned || 0} prev={report.yesterday.assigned || 0} color="#3b82f6" />
              <StatCard label="Upvotes" value={report.today.upvotes} prev={report.yesterday.upvotes} color="#a78bfa" />
            </div>
          </div>

          <div className="report-section" style={{ marginTop: 20 }}>
            <h3 className="subsection-title">This Week (from {report.week_start})</h3>
            <div className="report-stats-grid">
              <StatCard label="Total" value={report.this_week.total} />
              <StatCard label="Posts" value={report.this_week.posts} />
              <StatCard label="Comments" value={report.this_week.comments} />
              <StatCard label="Live" value={report.this_week.live} color="var(--live-green)" />
              <StatCard label="Deleted" value={report.this_week.deleted} color="var(--deleted-red)" />
              <StatCard label="To Be Assigned" value={report.this_week.imported || 0} color="#f59e0b" />
              <StatCard label="Assigned" value={report.this_week.assigned || 0} color="#3b82f6" />
              <StatCard label="Upvotes" value={report.this_week.upvotes} color="#a78bfa" />
            </div>
          </div>

          <div className="report-section" style={{ marginTop: 20 }}>
            <h3 className="subsection-title">All Time</h3>
            <div className="report-stats-grid">
              <StatCard label="Total Entries" value={report.total.total} />
              <StatCard label="Posts" value={report.total.posts} />
              <StatCard label="Comments" value={report.total.comments} />
              <StatCard label="Live" value={report.total.live} color="var(--live-green)" />
              <StatCard label="Deleted" value={report.total.deleted} color="var(--deleted-red)" />
              <StatCard label="To Be Assigned" value={report.total.imported || 0} color="#f59e0b" />
              <StatCard label="Assigned" value={report.total.assigned || 0} color="#3b82f6" />
              <StatCard label="Total Upvotes" value={report.total.upvotes} color="#a78bfa" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
